import { DataRef } from './DataRef';
import { IRootStore } from '../Root/RootStore';
import { ITreeEdge, TreeEdge } from './TreeEdge';
import { ITreeNode, TreeNode } from './TreeNode';
import { Instance, getRoot, types } from 'mobx-state-tree';

export const Tree = types
  .model('Tree', {
    nodes: types.optional(types.array(TreeNode), []),
    edges: types.optional(types.array(TreeEdge), []),
    dataRef: DataRef,
    namespace: types.maybeNull(types.string),
  })
  .actions(self => ({
    remove() {
      getRoot<IRootStore>(self).trees.removeTree(self.dataRef.dataId);
    }
  }))
  .views(self => ({
    findNodeByKey(nodeKey: string) {
      return self.nodes.find(node => node.key === nodeKey);
    },
    findNodesByDataRefType(dataRefType: string): ITreeNode[] {
      return self.nodes.filter(node => node.dataRef?.type === dataRefType);
    },
    getPreviousNode(nodeId: string): ITreeNode | undefined {
      return self.edges
        .find(edge => edge.next.key === nodeId)?.previous;
    },
    getEdgesFrom(nodeKey: string, edgeKey?: string | null): ITreeEdge[] {
      if (!edgeKey) {
        const edgesFrom = self.edges.filter(edge => edge.previousKey === nodeKey);
        return edgesFrom;
      }
      const edgesFrom = self.edges.filter(edge => edge.previousKey === nodeKey && edge.key === edgeKey);
      return edgesFrom;
    },
    findNextFrom(nodeKey: string, edgeKey?: string | null): ITreeNode | undefined {
      if (!edgeKey) {
        const node = self.edges.find(edge => edge.previousKey === nodeKey)?.next;
        return node;
      }
      const node = self.edges.find(edge => edge.previous.key === nodeKey && edge.key === edgeKey)?.next;
      return node;
    },
    findDefaultNextFrom(nodeKey: string): ITreeNode | undefined {
      const node = self.edges.find(edge => edge.previousKey === nodeKey && edge.default)?.next;
      return node;
    },
    isLeaf(nodeId: string): boolean {
      return !self.edges.some(edge => edge.previous.key === nodeId);
    },
    isRoot(nodeId: string): boolean {
      return !self.edges.some(edge => edge.next.key === nodeId);
    },
    get maxSteps(): number {
      const roots = self.nodes.filter(node => !self.edges.some(edge => edge.next.key === node.key));
      if (!roots) {
        console.warn('No root in the tree');
        return 0;
      }
      if (roots.length > 1) {
        console.warn('Invalid tree: multiple root nodes. ' + roots.map(root => root.key));
        return 0;
      }
      if (!roots.length) {
        console.warn('Root nodes cardinal is zero.');
        return 0;
      }
      const root = roots[0];
      // find the longest path in the tree given an array of edges
      const longestPath = (start: string, maxIter: number): number => {
        if(maxIter>100){
          return 0;
        }
        const next = self.edges.filter(edge => edge.previous.key === start);
        if (!next || !next.length) {
          return 1;
        }
        const pathLengths = next.map(edge => longestPath(edge.next.key, maxIter + 1));
        return 1 + Math.max(0, ...pathLengths);
      };
      const max = longestPath(root.key, 0);
      return max;
    },
  }));
export type ITree = Instance<typeof Tree>;
